import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

import { i18n } from "../../translate/i18n";
import KpiCard from "./KpiCard";
import ReportTable from "./ReportTable";
import { CHART_COLORS, formatDuration } from "./format";

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16 },
  kpis: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "16px 18px",
    minWidth: 0,
  },
  cardTitle: { fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 12 },
  empty: { fontSize: 14, color: theme.palette.text.secondary, padding: "24px 0", textAlign: "center" },
  note: { fontSize: 12, color: theme.palette.text.secondary, margin: "0 4px" },
  dot: { display: "inline-block", width: 10, height: 10, borderRadius: 5, marginRight: 8, verticalAlign: "middle" },
  link: { cursor: "pointer", color: theme.palette.primary.main, fontWeight: 600 },
}));

/**
 * Pestaña «Kanban» (doc 0447, D10): el embudo por etapa. «Ahora» es la foto de las tarjetas de
 * tickets no cerrados; «entradas» y «estancia» salen de los movimientos registrados por los
 * disparadores de TicketTags, que existen desde el 27-10-2026 (antes no había historial).
 */
const KanbanTab = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const stages = useMemo(() => data.kanban || [], [data.kanban]);
  const stuck = data.kanbanStuck || [];

  const totals = useMemo(() => {
    const cardsNow = stages.reduce((s, r) => s + r.cardsNow, 0);
    const entered = stages.reduce((s, r) => s + r.entered, 0);
    const byStay = stages.filter((r) => r.avgStaySeconds !== null).sort((a, b) => b.avgStaySeconds - a.avgStaySeconds)[0];
    const byAge = stages.filter((r) => r.avgAgeSeconds !== null).sort((a, b) => b.avgAgeSeconds - a.avgAgeSeconds)[0];
    return { cardsNow, entered, slowest: byStay || byAge, slowestByStay: Boolean(byStay) };
  }, [stages]);

  if (stages.length === 0) {
    return <div className={classes.card}><div className={classes.empty}>{i18n.t("reports.kanban.noBoard")}</div></div>;
  }

  const columns = [
    {
      key: "name",
      label: i18n.t("reports.kanban.stage"),
      sort: (r) => r.stageOrder,
      render: (r) => (<span><span className={classes.dot} style={{ backgroundColor: r.color || "#94A3B8" }} />{r.name}</span>),
    },
    { key: "cardsNow", label: i18n.t("reports.kanban.now"), align: "right" },
    { key: "avgAgeSeconds", label: i18n.t("reports.kanban.avgAge"), align: "right", render: (r) => formatDuration(r.avgAgeSeconds) },
    { key: "entered", label: i18n.t("reports.kanban.entered"), align: "right" },
    { key: "avgStaySeconds", label: i18n.t("reports.kanban.avgStay"), align: "right", render: (r) => formatDuration(r.avgStaySeconds) },
  ];

  const stuckColumns = [
    {
      key: "contactName",
      label: i18n.t("reports.kanban.contact"),
      sort: (r) => String(r.contactName || ""),
      render: (r) => (
        <span className={classes.link} onClick={() => r.ticketUuid && history.push(`/tickets/${r.ticketUuid}`)}>
          {r.contactName || `#${r.ticketId}`}
        </span>
      ),
    },
    {
      key: "stage",
      label: i18n.t("reports.kanban.stage"),
      render: (r) => (<span><span className={classes.dot} style={{ backgroundColor: r.color || "#94A3B8" }} />{r.stage}</span>),
    },
    { key: "userName", label: i18n.t("reports.team.agent"), render: (r) => r.userName || i18n.t("reports.team.unassigned") },
    { key: "ageMinutes", label: i18n.t("reports.kanban.since"), align: "right", render: (r) => formatDuration(r.ageMinutes * 60) },
  ];

  return (
    <div className={classes.grid}>
      <div className={classes.kpis}>
        <KpiCard live label={i18n.t("reports.kanban.cardsNow")} value={totals.cardsNow} />
        <KpiCard label={i18n.t("reports.kanban.enteredPeriod")} value={totals.entered} />
        <KpiCard
          label={i18n.t("reports.kanban.slowest")}
          value={totals.slowest ? totals.slowest.name : "—"}
          hint={
            totals.slowest
              ? i18n.t(totals.slowestByStay ? "reports.kanban.slowestByStay" : "reports.kanban.slowestByAge", {
                  value: formatDuration(totals.slowestByStay ? totals.slowest.avgStaySeconds : totals.slowest.avgAgeSeconds),
                })
              : null
          }
        />
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>{i18n.t("reports.kanban.funnel")}</div>
        <ResponsiveContainer width="100%" height={Math.max(180, stages.length * 38)}>
          <BarChart data={stages} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
            <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
            <Tooltip />
            <Bar dataKey="cardsNow" name={i18n.t("reports.kanban.now")} radius={[0, 4, 4, 0]}>
              {stages.map((s, i) => <Cell key={s.tagId} fill={s.color || CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ReportTable title={i18n.t("reports.kanban.detail")} columns={columns} rows={stages} defaultSort={null} rowKey="tagId" />
      <ReportTable title={i18n.t("reports.kanban.stuck")} columns={stuckColumns} rows={stuck} defaultSort="ageMinutes" rowKey="ticketId" />
      <p className={classes.note}>{i18n.t("reports.kanban.note")}</p>
    </div>
  );
};

export default KanbanTab;
